import { Link } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { apiMessage } from '../api/errors.ts';
import { planName } from '../plans/features.ts';
import { usePlans } from '../plans/store.ts';
import { toast } from '../state/feedback.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { type BetaGrant, betaDaysLeft, normalizeBetaCode, redeemBetaCode, shortDate } from './beta.ts';
import { useAccount } from './store.ts';

/** "Have a beta code?" link that opens a small redeem form (POST /v1/beta/redeem). */
export function BetaCodeLink({ onRedeemed }: { onRedeemed?: (grant: BetaGrant) => void }) {
  const signedIn = useAccount((s) => !!s.session);
  const catalog = usePlans((s) => s.catalog);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<BetaGrant | null>(null);

  if (done) {
    return (
      <p role="status" className="t-caption flex items-center gap-1.5 text-success">
        <Check size={14} aria-hidden /> Beta active: {planName(done.plan, catalog)} until {shortDate(done.endsAt)}
        {done.credits ? `, ${done.credits} test credits` : ''}.
      </p>
    );
  }
  if (!open) {
    return (
      <button
        type="button"
        className="t-caption self-start text-accent underline-offset-2 hover:underline"
        onClick={() => setOpen(true)}
      >
        Have a beta code?
      </button>
    );
  }
  if (!signedIn) {
    return (
      <p className="t-caption text-dim">
        <Link to="/signin" search={{ next: location.pathname }} className="text-accent underline">
          Sign in
        </Link>{' '}
        first, then enter your beta code.
      </p>
    );
  }
  return (
    <form
      className="flex flex-col gap-2"
      aria-label="Redeem a beta code"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          const grant = await redeemBetaCode(code);
          setDone(grant);
          toast(`Beta active until ${shortDate(grant.endsAt)}`, 'success');
          onRedeemed?.(grant);
        } catch (err) {
          setError(apiMessage(err, 'That code couldn’t be redeemed.'));
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="flex items-end gap-2">
        <Field
          label="Beta code"
          className="min-w-0 flex-1"
          placeholder="BETA-XXXX-XXXX"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onBlur={() => code && setCode(normalizeBetaCode(code))}
        />
        <Button type="submit" variant="primary" loading={busy} disabled={code.trim().length < 4}>
          Redeem
        </Button>
      </div>
      {error ? (
        <p role="alert" className="t-caption text-danger">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/** "Beta tester · Max until Nov 18" badge. */
export function BetaBadge() {
  const beta = useAccount((s) => s.me?.beta ?? null);
  const catalog = usePlans((s) => s.catalog);
  if (!beta) return null;
  return (
    <span className="t-caption self-start rounded-full bg-accent-soft px-2 py-0.5 text-accent" data-testid="beta-badge">
      Beta tester · {planName(beta.plan, catalog)} until {shortDate(beta.endsAt)}
    </span>
  );
}

/** Gentle "Beta ends in N days" banner in the last 7 days of a beta grant. */
export function BetaEndingBanner() {
  const beta = useAccount((s) => s.me?.beta ?? null);
  const days = betaDaysLeft(beta);
  if (!beta || days == null || days > 7) return null;
  return (
    <div
      role="status"
      className="t-caption flex flex-wrap items-center justify-between gap-2 rounded-md border border-[rgba(255,181,71,0.35)] bg-accent-soft px-3 py-2"
    >
      <span>
        {days <= 0 ? 'Your beta ends today.' : `Beta ends in ${days} ${days === 1 ? 'day' : 'days'}.`} Your projects
        stay; choose a plan to keep the features.
      </span>
      <Link to="/plans" className="font-semibold text-accent">
        See plans
      </Link>
    </div>
  );
}
